import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { highlightSnippet } from "@/lib/highlight-snippet";
import { formatInboxPreview, inboxPreviewMediaType } from "@/lib/inbox-media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useIsDesktop } from "@/hooks/use-mobile";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, Clock, FileSearch, Loader2, PanelLeftClose, PanelLeftOpen, Search, User, X, Zap } from "lucide-react";
import { InboxMediaIcon, InboxPresencePeer, InboxRow, MemberSourceBadge, PendingMessageRow, avatarColor, formatOpenAge, formatTimeCompact, getPadsplitProfileUrl, initials } from "./_shared";
import { InboxRowPresence } from "./InboxRowPresence";
import { TruncatedText } from "@/components/truncated-text";
import { MemberInfoSidebar } from "./MemberInfoSidebar";
import { MemberTimeline } from "./MemberTimeline";



// ─── Inbox Tab ────────────────────────────────────────────────────────────────
export const INBOX_PAGE_SIZE = 10;


export function InboxTab({
  filtered,
  allInboxRows,
  search,
  setSearch,
  isContentSearching,
  matchedChatIdByMember,
  matchedSnippetByMember,
  searchWords,
  searchMode,
  setSearchMode,
  timeFilter = "all",
  setTimeFilter,
  searchSinceMs = null,
  groupFilter,
  propertyFilterKey = "",
  serverHasMore = false,
  serverCapReached = false,
  onRequestMore,
  unresolvedOnly,
  hideUnresolvedFilter = false,
  setUnresolvedOnly,
  unresolvedTotal,
  selectedMemberId,
  setSelectedMemberId,
  focusChatId,
  focusThreadId,
  focusPendingId,
  canSend,
  toast,
  inboxLoading,
}: {
  filtered: InboxRow[];
  allInboxRows: InboxRow[];
  search: string;
  setSearch: (v: string) => void;
  isContentSearching: boolean;
  // Task #2071 — memberId → most recent matched chat id from the content
  // search endpoint. When the host clicks a member from a search-matched row,
  // we pass this chat id to setSelectedMemberId so the timeline scrolls
  // directly to the matched message instead of opening at the latest page.
  matchedChatIdByMember: Record<number, number>;
  // Task #2075 — memberId → short matched-message snippet from the server-side
  // content search. Rendered in place of lastChatPreview on rows that were
  // matched purely by message-content (so the host knows which message hit).
  matchedSnippetByMember: Record<number, string>;
  // Lower-cased query words used to highlight the keyword inside the snippet.
  searchWords: string[];
  // Search mode: "people" = in-memory name/property match only (no in-thread
  // highlighting); "messages" = full combined-corpus keyword search with the
  // in-thread match navigator.
  searchMode: "people" | "messages";
  setSearchMode: (mode: "people" | "messages") => void;
  // Time-window filter for message search. Passed down to MemberTimeline so
  // the match-navigator also respects the selected window.
  timeFilter?: "7d" | "30d" | "90d" | "all";
  setTimeFilter?: (f: "7d" | "30d" | "90d" | "all") => void;
  searchSinceMs?: number | null;
  groupFilter: string;
  // Stable key for the property multi-select filter (joined ids) — only used
  // to reset the reveal pagination when the filter changes.
  propertyFilterKey?: string;
  // True when the server may hold conversations older than what's loaded
  // (the recency page came back full and the fetch limit isn't maxed out).
  serverHasMore?: boolean;
  // True when the fetch limit hit the server-side cap with a full page — older
  // conversations exist but can only be reached via search.
  serverCapReached?: boolean;
  // Ask the parent to grow the inbox fetch limit (load older conversations).
  // Omitted by callers that render a fully-loaded list (e.g. tests).
  onRequestMore?: () => void;
  unresolvedOnly: boolean;
  hideUnresolvedFilter?: boolean;
  setUnresolvedOnly: (v: boolean) => void;
  // Total number of members with open (unresolved) messages, from the
  // server-side unresolved inbox endpoint. Surfaced as a count on the toggle
  // label. Null until the unresolved query has loaded (it only fetches while
  // the toggle is on).
  unresolvedTotal: number | null;
  selectedMemberId: number | null;
  setSelectedMemberId: (id: number | null, focusChatId?: number | null) => void;
  // Forwarded from /app/messages?chat=N deep links so the timeline can
  // scroll-to + highlight the right bubble.
  focusChatId: number | null;
  // Forwarded from /app/messages?thread=N deep links so the thread panel
  // opens + highlights the specific note.
  focusThreadId: number | null;
  // PadSplit send-UX — forwarded from /app/messages?pending=P failed-send deep
  // links so MemberTimeline scrolls to + highlights the exact failed bubble.
  focusPendingId?: number | null;
  canSend: boolean;
  toast: ReturnType<typeof useToast>["toast"];
  inboxLoading: boolean;
}) {
  const queryClient = useQueryClient();
  const [actionItemsOpen, setActionItemsOpen] = useState(true);
  // Pagination state — reset only when the user explicitly changes the search
  // query or the property group filter (not on background inbox data refreshes).
  // Persist the page across navigation so a host who scrolled to page 3, opened
  // a conversation, and came back lands where they left off.
  const INBOX_PAGE_STORAGE_KEY = "padpulse:inbox:page";
  const [inboxPage, setInboxPage] = useState<number>(() => {
    if (typeof window === "undefined") return 1;
    const raw = window.sessionStorage.getItem(INBOX_PAGE_STORAGE_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  });
  // Skip the initial mount so we don't clobber the restored page from
  // sessionStorage; only reset on subsequent user-driven changes to search
  // or group filter.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setInboxPage(1);
  }, [search, groupFilter, propertyFilterKey]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(INBOX_PAGE_STORAGE_KEY, String(inboxPage));
  }, [inboxPage]);

  const isSearching = search.trim().length > 0;
  // Show every conversation (no client-side paging) when searching OR when the
  // "unresolved only" filter is active, so the host sees ALL open threads in the
  // scrollable rail instead of just the first page. The "Show more" pager is
  // only used for the unfiltered full inbox, where the row count is unbounded.
  const showAllRows = isSearching || unresolvedOnly;
  const visibleRows = showAllRows ? filtered : filtered.slice(0, inboxPage * INBOX_PAGE_SIZE);
  // Rows already loaded but not yet revealed by the reveal pager.
  const hasMoreLocal = !showAllRows && filtered.length > inboxPage * INBOX_PAGE_SIZE;
  // Older conversations the server hasn't sent yet (recency page came back
  // full). Search and the unresolved filter fetch their own complete lists, so
  // the load-older affordance only applies to the plain browse list.
  const hasMoreRemote = !showAllRows && serverHasMore;
  const hasMore = hasMoreLocal || hasMoreRemote;
  // Advance the list: reveal already-loaded rows first; once those are all
  // visible, grow the server fetch limit so older conversations stream in.
  const handleShowMore = useCallback(() => {
    if (hasMoreLocal) setInboxPage((p) => p + 1);
    else if (hasMoreRemote) onRequestMore?.();
  }, [hasMoreLocal, hasMoreRemote, onRequestMore]);
  // Infinite scroll — auto-click "Show more" whenever the sentinel button
  // scrolls into view (200px early), so the host can just keep scrolling.
  // The button stays rendered as an explicit fallback/affordance.
  const handleShowMoreRef = useRef(handleShowMore);
  handleShowMoreRef.current = handleShowMore;
  const showMoreButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const el = showMoreButtonRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) handleShowMoreRef.current();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore]);
  // Default the member info panel open on lg+ screens (where it sits beside the
  // conversation timeline) and closed on smaller viewports (where it acts like
  // a modal drawer that would otherwise cover the conversation on tap).
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  // Inbox-list collapse: open by default on every viewport, but the user can
  // hide it from the timeline header to give the conversation more room.
  const [inboxListOpen, setInboxListOpen] = useState(true);
  const toggleSidebarButtonRef = useRef<HTMLButtonElement>(null);
  const toggleInboxListButtonRef = useRef<HTMLButtonElement>(null);
  // Search-mode toggle visibility. The People/Messages pills normally only
  // appear once the host starts typing, so a non-default mode (e.g. "Messages")
  // would otherwise be invisible after the search box is cleared. When that
  // happens we surface a persistent reminder chip; clicking it force-opens the
  // toggle and briefly flashes it so the host can switch modes (Task #3659).
  const [modeToggleOpen, setModeToggleOpen] = useState(false);
  const [modeToggleFlash, setModeToggleFlash] = useState(false);
  const modeToggleFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openModeToggle = useCallback(() => {
    setModeToggleOpen(true);
    setModeToggleFlash(true);
    if (modeToggleFlashTimerRef.current) clearTimeout(modeToggleFlashTimerRef.current);
    modeToggleFlashTimerRef.current = setTimeout(() => setModeToggleFlash(false), 1500);
  }, []);
  useEffect(() => () => {
    if (modeToggleFlashTimerRef.current) clearTimeout(modeToggleFlashTimerRef.current);
  }, []);
  // Collapse a force-opened toggle back to its default hidden state once the
  // host returns to the default "people" mode with an empty search box, so the
  // force-open from the reminder chip stays temporary instead of pinning the
  // toggle visible forever.
  useEffect(() => {
    if (!search.trim() && searchMode === "people") setModeToggleOpen(false);
  }, [search, searchMode]);

  // Auto-advance: when the current member's unresolved count drops from >0 to 0,
  // automatically switch to the next member with the oldest unresolved message
  // (longest waiting) so hosts can triage open threads without manual clicking.
  // The ref tracks both memberId and count so a manual member switch never
  // triggers a false advance.
  const prevUnresolvedCountRef = useRef<{ memberId: number; count: number } | null>(null);
  useEffect(() => {
    if (selectedMemberId == null) {
      prevUnresolvedCountRef.current = null;
      return;
    }
    const current = filtered.find((r) => r.memberId === selectedMemberId);
    const currentCount = current?.unresolvedCount ?? 0;

    // If we just switched members, record the baseline and bail (don't auto-advance
    // just because the newly-selected member already has zero open messages).
    if (prevUnresolvedCountRef.current?.memberId !== selectedMemberId) {
      prevUnresolvedCountRef.current = { memberId: selectedMemberId, count: currentCount };
      return;
    }

    const prevCount = prevUnresolvedCountRef.current.count;
    prevUnresolvedCountRef.current = { memberId: selectedMemberId, count: currentCount };

    // Only advance on a transition from having open messages to none.
    if (prevCount <= 0 || currentCount > 0) return;

    const candidates = filtered
      .filter((r) => r.memberId !== selectedMemberId && r.unresolvedCount > 0)
      .sort((a, b) => {
        const aTime = a.oldestUnresolvedAt ? new Date(a.oldestUnresolvedAt).getTime() : Infinity;
        const bTime = b.oldestUnresolvedAt ? new Date(b.oldestUnresolvedAt).getTime() : Infinity;
        return aTime - bTime;
      });

    if (candidates.length > 0) {
      setSelectedMemberId(candidates[0].memberId);
    }
  }, [selectedMemberId, filtered, setSelectedMemberId]);

  const actionItemsQuery = useQuery<PendingMessageRow[]>({
    queryKey: ["/api/app/messages/pending"],
  });
  const actionItems = useMemo(
    () => (actionItemsQuery.data ?? []).filter((r) => r.autoSend === false),
    [actionItemsQuery.data],
  );

  // Inbox-level co-presence — which teammates are currently viewing/typing in
  // each conversation. Polls on the ~4.5s cadence (between the per-conversation
  // poll/heartbeat) so a row's "teammate is here" badge tracks the same 15s
  // stale window the server applies. Pauses while the tab is hidden.
  const presenceQuery = useQuery<{ presence: Record<string, InboxPresencePeer[]> }>({
    queryKey: ["/api/app/inbox/presence"],
    refetchInterval: 4500,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  const presenceByMember = presenceQuery.data?.presence ?? {};

  const confirmMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/messages/pending/${id}/confirm`),
    onSuccess: () => {
      toast({ title: "Approved for send" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
    },
    onError: (err) => toast({ title: "Couldn't approve", description: parseErrorMessage(err, "Couldn't approve. Please try again."), variant: "destructive" }),
  });

  const cancelMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/messages/pending/${id}/cancel`),
    onSuccess: () => {
      toast({ title: "Cancelled" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
    },
    onError: (err) => toast({ title: "Couldn't cancel", description: parseErrorMessage(err, "Couldn't cancel. Please try again."), variant: "destructive" }),
  });

  const isDesktop = useIsDesktop();

  // ── Per-user panel-size persistence (desktop only) ──────────────────────────
  // Read once synchronously so defaultSize can use it immediately (no flash).
  const savedLayoutRef = useRef<number[] | null>(null);
  const userQuery = useQuery<{ user: { id: number } }>({
    queryKey: ["/api/app/auth/me"],
    staleTime: 5 * 60 * 1000,
  });
  const userId = userQuery.data?.user?.id;

  if (savedLayoutRef.current === null && userId != null && typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem(`padpulse:messages:panelSizes:${userId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every((n: unknown) => typeof n === "number" && n > 0)) {
          savedLayoutRef.current = parsed;
        }
      }
    } catch {}
  }
  const savedLayout = savedLayoutRef.current;

  // Imperative handles for programmatic collapse/expand via toggle buttons
  const inboxPanelRef = useRef<ImperativePanelHandle>(null);
  const sidebarPanelRef = useRef<ImperativePanelHandle>(null);

  // Debounced localStorage write so dragging doesn't thrash storage.
  const layoutWriteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const handleLayoutChange = useCallback((sizes: number[]) => {
    const key = userId == null ? null : `padpulse:messages:panelSizes:${userId}`;
    if (!key || typeof window === "undefined") return;
    const merged: number[] = new Array(3);
    const prev = savedLayoutRef.current;
    for (let i = 0; i < 3; i++) {
      merged[i] =
        sizes[i] ??
        (prev && prev[i] > 0 ? prev[i] : null) ??
        (i === 0 ? 25 : i === 1 ? 50 : 25);
    }
    savedLayoutRef.current = merged;
    const json = JSON.stringify(merged);
    if (lastSavedRef.current === json) return; // dedupe identical values
    if (layoutWriteTimerRef.current) clearTimeout(layoutWriteTimerRef.current);
    layoutWriteTimerRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(key, json);
        lastSavedRef.current = json;
      } catch {}
    }, 200);
  }, [userId]);

  // Pointer-events safety net: react-resizable-panels sets pointer-events:none
  // on document.body during drag. If a mouseup is lost (e.g. mouse escapes the
  // Replit proxy iframe, tab switch, or window blur), the body stays permanently
  // unclickable — all sidebar navigation and page interactions silently drop.
  //
  // Three-layer recovery:
  //  1. pointerup  — normal drag-end; clears after 100 ms so the library can
  //                  finish its own cleanup first.
  //  2. mousemove  — window-level mousemove still fires even with pointer-events
  //                  none on body (window is above the CSS hit-test); if the
  //                  pointer is moving the drag is over, so clear immediately.
  //                  Debounced to avoid a tight loop.
  //  3. blur       — window loses focus (unlikely in the iframe case, but kept
  //                  as the original 2 s delayed fallback).
  //  4. unmount    — navigating away from Messages clears any stuck state so it
  //                  can never persist to other pages.
  const pointerEventsFixRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    function clearStuck() {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    }
    function scheduleFix() {
      if (pointerEventsFixRef.current) clearTimeout(pointerEventsFixRef.current);
      pointerEventsFixRef.current = setTimeout(clearStuck, 2000);
    }
    function onPointerUp() {
      // Small delay to let react-resizable-panels clean up itself first
      if (pointerEventsFixRef.current) clearTimeout(pointerEventsFixRef.current);
      pointerEventsFixRef.current = setTimeout(clearStuck, 100);
    }
    function onMouseMove() {
      // If pointer-events is stuck and the mouse is moving, the drag ended
      // outside the iframe. Clear immediately (no delay needed — mouse movement
      // proves we are no longer in an active drag).
      if (document.body.style.pointerEvents === "none") {
        if (pointerEventsFixRef.current) clearTimeout(pointerEventsFixRef.current);
        clearStuck();
      }
    }
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("blur", scheduleFix);
    return () => {
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("blur", scheduleFix);
      if (pointerEventsFixRef.current) clearTimeout(pointerEventsFixRef.current);
      // Always restore on unmount — navigating away from the Messages page must
      // never leave a stuck pointer-events:none on the rest of the app.
      clearStuck();
    };
  }, []);

  // Toggle buttons drive the *imperative panel handles* on desktop so collapse/
  // expand is animated by react-resizable-panels rather than a state snap.
  const handleToggleInboxList = useCallback(() => {
    if (isDesktop && inboxPanelRef.current) {
      if (inboxPanelRef.current.isCollapsed()) {
        inboxPanelRef.current.expand();
      } else {
        inboxPanelRef.current.collapse();
      }
    } else {
      setInboxListOpen((o) => !o);
    }
  }, [isDesktop]);

  const handleToggleSidebar = useCallback(() => {
    if (isDesktop && sidebarPanelRef.current) {
      if (sidebarPanelRef.current.isCollapsed()) {
        sidebarPanelRef.current.expand();
      } else {
        sidebarPanelRef.current.collapse();
      }
    } else {
      setSidebarOpen((o) => !o);
    }
  }, [isDesktop]);

  const handleCloseSidebar = useCallback(() => {
    if (isDesktop && sidebarPanelRef.current) {
      sidebarPanelRef.current.collapse();
    } else {
      setSidebarOpen(false);
    }
    // Restore focus to the toggle button (only meaningful on small screens
    // where the sidebar acts like a modal drawer; harmless on lg+).
    if (typeof window === "undefined") return;
    if (window.matchMedia("(min-width: 1024px)").matches) return;
    requestAnimationFrame(() => {
      toggleSidebarButtonRef.current?.focus();
    });
  }, [isDesktop]);

  // ── Reusable panel content ────────────────────────────────────────────────
  const inboxSidebar = (
    <aside
      id="inbox-sidebar"
      className={`flex flex-col bg-white w-full ${!isDesktop ? "border-r border-gray-200" : ""} ${inboxListOpen ? "sm:flex" : "sm:hidden"} ${selectedMemberId == null ? "flex" : "hidden sm:flex"}`}
      data-testid="inbox-sidebar"
      data-tour="messages-list"
    >
      <div className="border-b border-gray-200 p-3" data-tour="messages-search">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            {isContentSearching ? (
              <div className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
            ) : (
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            )}
            <Input
              ref={searchInputRef}
              placeholder={searchMode === "people" ? "Search by name or property" : "Search message content"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="input-search-members"
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="hidden h-8 w-8 shrink-0 p-0 text-gray-500 sm:inline-flex"
            onClick={() => {
              handleToggleInboxList();
              requestAnimationFrame(() => {
                toggleInboxListButtonRef.current?.focus();
              });
            }}
            data-testid="button-collapse-inbox-list"
            title="Hide member list"
            aria-controls="inbox-sidebar"
            aria-expanded={true}
          >
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>
        {/* Search mode toggle — visible while a search is active, or when
            force-opened from the persistent mode reminder chip below. */}
        {(search.trim() || modeToggleOpen) && (
          <div
            className={`mt-2 flex w-fit items-center gap-1 rounded-full transition-shadow ${
              modeToggleFlash ? "ring-2 ring-brand ring-offset-1" : ""
            }`}
            role="group"
            aria-label="Search mode"
          >
            <button
              type="button"
              onClick={() => setSearchMode("people")}
              aria-pressed={searchMode === "people"}
              data-testid="button-search-mode-people"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                searchMode === "people"
                  ? "border-brand bg-brand text-white"
                  : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <User className="h-3 w-3" />
              People
            </button>
            <button
              type="button"
              onClick={() => setSearchMode("messages")}
              aria-pressed={searchMode === "messages"}
              data-testid="button-search-mode-messages"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                searchMode === "messages"
                  ? "border-brand bg-brand text-white"
                  : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <FileSearch className="h-3 w-3" />
              Messages
            </button>
          </div>
        )}
        {/* Persistent active-mode reminder (Task #3659) — when a non-default
            search mode is active but the toggle is hidden (search box empty
            and not force-opened), surface a compact chip so the host knows the
            next search fires a server content-search. Clicking it re-opens and
            flashes the toggle. */}
        {!search.trim() && !modeToggleOpen && searchMode !== "people" && (
          <div className="mt-2">
            <button
              type="button"
              onClick={openModeToggle}
              data-testid="badge-active-search-mode"
              title="Active search mode — click to change"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand bg-brand-muted px-2.5 py-1 text-xs font-medium text-brand transition-colors hover:bg-brand-muted/80"
            >
              <FileSearch className="h-3 w-3" />
              Messages mode
            </button>
          </div>
        )}
        {/* Unresolved-only filter (Task #2994 / #4325) — when toggled on the
            parent fetches from the server-side unresolved endpoint so ALL
            members with open messages appear, not just those in the top-100
            recency window. See app-messages.tsx unresolvedInboxQuery. */}
        <div className={`mt-2 ${hideUnresolvedFilter ? "hidden" : ""}`}>
          <button
            type="button"
            onClick={() => setUnresolvedOnly(!unresolvedOnly)}
            aria-pressed={unresolvedOnly}
            data-testid="button-filter-unresolved-only"
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
              unresolvedOnly
                ? "border-brand bg-brand text-white"
                : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            {unresolvedOnly ? (
              <CheckCircle className="h-3.5 w-3.5" />
            ) : (
              <div className="h-3.5 w-3.5 rounded-full border-2 border-current" />
            )}
            Unresolved messages only
            {unresolvedTotal != null && unresolvedTotal > 0 && (
              <span data-testid="text-unresolved-total">({unresolvedTotal})</span>
            )}
          </button>
        </div>
        {/* Time-window filter — only visible while a search is active in messages mode */}
        {search.trim() && searchMode === "messages" && setTimeFilter && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Search time range">
              {(["7d", "30d", "90d", "all"] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setTimeFilter(opt)}
                  data-testid={`button-time-filter-${opt}`}
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                    timeFilter === opt
                      ? "border-brand bg-brand text-white"
                      : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {opt === "7d" ? "7 days" : opt === "30d" ? "30 days" : opt === "90d" ? "90 days" : "All time"}
                </button>
              ))}
            </div>
            {timeFilter !== "all" && (
              <p className="text-[11px] text-gray-400">
                Searching {timeFilter === "7d" ? "last 7 days" : timeFilter === "30d" ? "last 30 days" : "last 90 days"}
              </p>
            )}
          </div>
        )}
      </div>
      <ScrollArea className="flex-1">
        {inboxLoading ? (
          <div className="flex h-32 items-center justify-center text-gray-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400" data-testid="text-inbox-empty">
            {unresolvedOnly ? "No members with open messages." : "No conversations yet."}
          </div>
        ) : (
          <>
            {visibleRows.map((row) => (
              <button
                key={row.memberId}
                onClick={() => setSelectedMemberId(row.memberId, searchMode === "messages" ? (matchedChatIdByMember[row.memberId] ?? null) : null)}
                className={`flex w-full items-start gap-2.5 border-b border-gray-100 p-3 text-left transition-colors hover:bg-gray-50 ${
                  selectedMemberId === row.memberId ? "bg-blue-50" : ""
                }`}
                data-testid={`row-inbox-${row.memberId}`}
              >
                {/* Avatar */}
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${avatarColor(row.memberName)}`}
                >
                  {initials(row.memberName)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <TruncatedText
                        text={row.memberName}
                        className="text-sm font-semibold text-gray-900"
                        data-testid={`text-member-name-${row.memberId}`}
                      >
                        {highlightSnippet(row.memberName, searchWords)}
                      </TruncatedText>
                      {/* PadSplit vs Direct member badge — drives at-a-glance segmentation in the inbox list. */}
                      {row.isBookingRequest && (
                        <Badge
                          variant="secondary"
                          className="shrink-0 bg-yellow-400 text-yellow-900 border-yellow-500 text-[10px] leading-none font-semibold"
                          data-testid={`badge-request-${row.memberId}`}
                        >
                          Request
                        </Badge>
                      )}
                      <MemberSourceBadge
                        padsplitMemberId={row.padsplitMemberId}
                        memberId={row.memberId}
                        compact
                        href={getPadsplitProfileUrl({ occupantId: row.occupantId })}
                      />
                      {row.unresolvedCount > 0 && (
                        <Badge
                          className="shrink-0 border-transparent bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent-hover text-[10px] leading-none"
                          data-testid={`badge-unresolved-${row.memberId}`}
                        >
                          {row.unresolvedCount} open
                        </Badge>
                      )}
                      {row.unresolvedCount > 0 && row.oldestUnresolvedAt && (
                        <span
                          className="shrink-0 text-[10px] font-medium text-gray-400"
                          data-testid={`text-oldest-open-age-${row.memberId}`}
                          title={`Oldest unresolved message has been open for ${formatOpenAge(row.oldestUnresolvedAt)}`}
                        >
                          {formatOpenAge(row.oldestUnresolvedAt)}
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <InboxRowPresence peers={presenceByMember[String(row.memberId)] ?? []} memberId={row.memberId} />
                      <span className="text-[11px] text-gray-400">{formatTimeCompact(row.lastChatAt)}</span>
                    </div>
                  </div>
                  {row.propertyName && (
                    <TruncatedText as="div" text={row.propertyName} className="text-xs text-gray-500" />
                  )}
                  <div className="mt-0.5 flex items-center gap-1.5">
                    {(() => {
                      // Task #2075 — when a search is active and this row was matched
                      // via server-side content search (not just name/property/preview),
                      // show the matched message snippet with the keyword highlighted
                      // instead of the most-recent preview. Lets the host confirm which
                      // message hit before opening the thread.
                      const snippet = matchedSnippetByMember[row.memberId];
                      const previewLower = (row.lastChatPreview ?? "").toLowerCase();
                      const nameLower = String(row.memberName ?? "").toLowerCase();
                      const propertyLower = (row.propertyName ?? "").toLowerCase();
                      const isInMemoryMatch =
                        searchWords.length > 0 &&
                        searchWords.every(
                          (w) =>
                            nameLower.includes(w) ||
                            propertyLower.includes(w) ||
                            previewLower.includes(w),
                        );
                      if (searchWords.length > 0 && snippet && !isInMemoryMatch) {
                        return (
                          <span
                            className="truncate text-xs text-gray-500"
                            data-testid={`text-matched-snippet-${row.memberId}`}
                          >
                            {highlightSnippet(snippet, searchWords)}
                          </span>
                        );
                      }
                      const mediaType = inboxPreviewMediaType(row.lastChatPreview);
                      // Highlight the matched keyword inside the preview text
                      // only when the row actually matched in memory (name or
                      // preview). Media-only previews ("Image", etc.) and
                      // content-only matches naturally fall back to plain text
                      // since highlightSnippet returns the string unchanged
                      // when no word is present.
                      const previewText = formatInboxPreview(row.lastChatPreview, "(no messages)");
                      return (
                        <span className="flex items-center gap-1 truncate text-xs text-gray-500">
                          {row.lastChatSenderType === "admin" ? "You: " : ""}
                          {mediaType && (
                            <InboxMediaIcon type={mediaType} className="h-3 w-3 shrink-0" />
                          )}
                          {mediaType ? previewText : highlightSnippet(previewText, searchWords)}
                        </span>
                      );
                    })()}
                    {row.pendingCount > 0 && (
                      <Badge variant="secondary" className="shrink-0 text-[10px] leading-none" data-testid={`badge-pending-${row.memberId}`}>
                        {row.pendingCount}
                      </Badge>
                    )}
                    {/* PadSplit send-UX — terminally-failed outbound(s) on this
                        thread. Visible while browsing; clicking the row opens
                        the conversation where the failed bubble is in reach. */}
                    {(row.failedCount ?? 0) > 0 && (
                      <Badge
                        variant="destructive"
                        className="shrink-0 gap-0.5 text-[10px] leading-none"
                        data-testid={`badge-failed-${row.memberId}`}
                        title={`${row.failedCount} message${(row.failedCount ?? 0) === 1 ? "" : "s"} failed to send`}
                      >
                        <AlertCircle className="h-2.5 w-2.5" />
                        {row.failedCount}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {hasMore && (
              <button
                ref={showMoreButtonRef}
                onClick={handleShowMore}
                className="flex w-full items-center justify-center gap-1.5 border-b border-gray-100 py-2.5 text-xs font-medium text-blue-600 hover:bg-gray-50"
                data-testid="button-inbox-show-more"
              >
                {hasMoreLocal ? (
                  <>
                    Show more
                    <span className="text-gray-400">
                      ({filtered.length - visibleRows.length}
                      {hasMoreRemote ? "+" : ""} remaining)
                    </span>
                  </>
                ) : (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading older conversations…
                  </>
                )}
              </button>
            )}
            {/* The fetch limit hit the server cap with a full page — older
                conversations exist but scrolling can't reach them. Point the
                host at search, which covers the full history. */}
            {!hasMore && !showAllRows && serverCapReached && (
              <div
                className="border-b border-gray-100 px-3 py-2.5 text-center text-xs text-gray-500"
                data-testid="text-inbox-use-search-hint"
              >
                Not seeing what you're looking for?{" "}
                <button
                  onClick={() => searchInputRef.current?.focus()}
                  className="font-medium text-blue-600 hover:underline"
                  data-testid="button-inbox-use-search"
                >
                  Use search
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Action Items panel ── */}
        {actionItems.length > 0 && (
          <div className="border-t border-gray-200" data-testid="action-items-panel">
            <button
              className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
              onClick={() => setActionItemsOpen((o) => !o)}
              data-testid="button-toggle-action-items"
            >
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                Action Items
                <Badge variant="secondary" className="text-[10px] leading-none" data-testid="badge-action-items-count">
                  {actionItems.length}
                </Badge>
              </span>
              {actionItemsOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {actionItemsOpen && (
              <div className="divide-y divide-gray-100">
                {actionItems.map((item) => (
                  <div key={item.id} className="px-3 py-2" data-testid={`action-item-${item.id}`}>
                    <div className="mb-1 flex items-start justify-between gap-1">
                      <span className="text-xs font-medium text-gray-800 line-clamp-2">
                        {item.messageText}
                      </span>
                    </div>
                    <div className="mb-1.5 flex flex-wrap items-center gap-1 text-[10px] text-gray-400">
                      {item.ruleName && (
                        <span className="flex items-center gap-0.5 text-purple-600">
                          <Zap className="h-2.5 w-2.5" /> {item.ruleName}
                        </span>
                      )}
                      {item.sendAt && (
                        <span>{new Date(item.sendAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => confirmMutation.mutate(item.id)}
                        disabled={confirmMutation.isPending}
                        data-testid={`button-action-approve-${item.id}`}
                      >
                        <CheckCircle className="mr-1 h-3 w-3" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] text-red-500 hover:text-red-700"
                        onClick={() => cancelMutation.mutate(item.id)}
                        disabled={cancelMutation.isPending}
                        data-testid={`button-action-cancel-${item.id}`}
                      >
                        <X className="mr-1 h-3 w-3" /> Dismiss
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </aside>
  );

  const chatTimeline = (
    <main className={`${selectedMemberId == null ? "hidden sm:flex" : "flex"} flex-1 flex-col bg-gray-50 overflow-hidden min-h-0 w-full`}>
      {selectedMemberId == null ? (
        <>
          {!inboxListOpen && (
            <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-2.5">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-xs text-gray-600"
                onClick={handleToggleInboxList}
                data-testid="button-expand-inbox-list"
                title="Show member list"
                ref={toggleInboxListButtonRef}
                aria-controls="inbox-sidebar"
                aria-expanded={false}
              >
                <PanelLeftOpen className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Member list</span>
              </Button>
            </div>
          )}
          <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
            Select a member to view their conversation.
          </div>
        </>
      ) : (
        <MemberTimeline
          memberId={selectedMemberId}
          memberName={allInboxRows.find((r) => r.memberId === selectedMemberId)?.memberName ?? filtered.find((r) => r.memberId === selectedMemberId)?.memberName ?? ""}
          memberPadsplitId={allInboxRows.find((r) => r.memberId === selectedMemberId)?.padsplitMemberId ?? filtered.find((r) => r.memberId === selectedMemberId)?.padsplitMemberId ?? null}
          propertyName={allInboxRows.find((r) => r.memberId === selectedMemberId)?.propertyName ?? filtered.find((r) => r.memberId === selectedMemberId)?.propertyName ?? null}
          focusChatId={focusChatId}
          focusThreadId={focusThreadId}
          focusPendingId={focusPendingId}
          canSend={canSend}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
            queryClient.invalidateQueries({ queryKey: ["/api/app/members", selectedMemberId, "timeline"] });
          }}
          toast={toast}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={handleToggleSidebar}
          toggleSidebarButtonRef={toggleSidebarButtonRef}
          inboxListOpen={inboxListOpen}
          onToggleInboxList={handleToggleInboxList}
          toggleInboxListButtonRef={toggleInboxListButtonRef}
          onBack={() => setSelectedMemberId(null)}
          searchWords={searchMode === "messages" ? searchWords : []}
          activeSearchTerm={searchMode === "messages" ? search : ""}
          searchSinceMs={searchMode === "messages" ? (searchSinceMs ?? undefined) : undefined}
        />
      )}
    </main>
  );

  const memberInfoSidebarDesktop = selectedMemberId != null && sidebarOpen && (
    <MemberInfoSidebar
      memberId={selectedMemberId}
      fallbackName={allInboxRows.find((r) => r.memberId === selectedMemberId)?.memberName ?? filtered.find((r) => r.memberId === selectedMemberId)?.memberName ?? null}
      fallbackPropertyName={allInboxRows.find((r) => r.memberId === selectedMemberId)?.propertyName ?? filtered.find((r) => r.memberId === selectedMemberId)?.propertyName ?? null}
      onClose={handleCloseSidebar}
      className="static w-full"
    />
  );

  const memberInfoSidebarMobile = selectedMemberId != null && sidebarOpen && (
    <MemberInfoSidebar
      memberId={selectedMemberId}
      fallbackName={allInboxRows.find((r) => r.memberId === selectedMemberId)?.memberName ?? filtered.find((r) => r.memberId === selectedMemberId)?.memberName ?? null}
      fallbackPropertyName={allInboxRows.find((r) => r.memberId === selectedMemberId)?.propertyName ?? filtered.find((r) => r.memberId === selectedMemberId)?.propertyName ?? null}
      onClose={handleCloseSidebar}
    />
  );

  // Keep React state in sync with panel collapse/expand (desktop only).
  const lastInboxListOpenRef = useRef(inboxListOpen);
  useEffect(() => {
    if (!isDesktop || !inboxPanelRef.current) return;
    if (lastInboxListOpenRef.current !== inboxListOpen) {
      lastInboxListOpenRef.current = inboxListOpen;
      if (inboxListOpen) inboxPanelRef.current.expand();
      else inboxPanelRef.current.collapse();
    }
  }, [isDesktop, inboxListOpen]);

  const lastSidebarOpenRef = useRef(sidebarOpen);
  useEffect(() => {
    if (!isDesktop || !sidebarPanelRef.current) return;
    if (lastSidebarOpenRef.current !== sidebarOpen) {
      lastSidebarOpenRef.current = sidebarOpen;
      if (sidebarOpen) sidebarPanelRef.current.expand();
      else sidebarPanelRef.current.collapse();
    }
  }, [isDesktop, sidebarOpen]);

  // Force inbox expanded when no member is selected (desktop).
  useEffect(() => {
    if (!isDesktop || !inboxPanelRef.current) return;
    if (selectedMemberId == null) inboxPanelRef.current.expand();
  }, [isDesktop, selectedMemberId]);

  return (
    <div className="relative flex h-full overflow-hidden">
      {isDesktop ? (
        <ResizablePanelGroup
          direction="horizontal"
          className="flex h-full w-full"
          onLayout={(sizes) => handleLayoutChange(sizes)}
        >
          <ResizablePanel
            ref={inboxPanelRef}
            defaultSize={savedLayout?.[0] ?? 25}
            minSize={15}
            maxSize={55}
            collapsible
            onCollapse={() => {
              setInboxListOpen(false);
            }}
            onExpand={() => {
              setInboxListOpen(true);
            }}
            className="flex flex-col"
          >
            {inboxSidebar}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel
            defaultSize={savedLayout?.[1] ?? 50}
            minSize={30}
            className="flex flex-col"
          >
            {chatTimeline}
          </ResizablePanel>
          {selectedMemberId != null && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel
                ref={sidebarPanelRef}
                defaultSize={savedLayout?.[2] ?? 25}
                minSize={15}
                maxSize={40}
                collapsible
                onCollapse={() => {
                  setSidebarOpen(false);
                }}
                onExpand={() => {
                  setSidebarOpen(true);
                }}
                className="flex flex-col"
              >
                {memberInfoSidebarDesktop}
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      ) : (
        <>
          {(inboxListOpen || selectedMemberId == null) && inboxSidebar}
          {chatTimeline}
          {memberInfoSidebarMobile}
        </>
      )}
    </div>
  );
}
